import * as React from 'react';
import nb from 'date-fns/locale/nb';
import { useId, useLayoutEffect } from '@finn-no/troika-react-utils';
import { isBefore, isSameDay, addDays, subDays, format, parse, isValid, isSameMonth, isWithinInterval, isToday, getDate, eachDayOfInterval, getWeekOfMonth, differenceInCalendarDays, startOfWeek, eachWeekOfInterval, startOfMonth, lastDayOfMonth, endOfWeek, addMonths, subMonths, startOfToday, eachMonthOfInterval, differenceInCalendarMonths, endOfMonth } from 'date-fns';
import { useEffectReducer } from 'use-effect-reducer';
import { getOwnerDocument, forwardRefWithAs, useForkedRef, wrapEvent } from '@reach/utils';
import classNames from 'classnames';
import Popover from '@reach/popover';

var roleDescription = 'datovelger';
var jumpToPrevMonth = 'Gå til forrige måned.';
var jumpToNextMonth = 'Gå til neste måned.';

var dateIsSelected = function dateIsSelected(date) {
  return "Valgt dato: " + date;
};

var dateIsSelectedAsStartDate = function dateIsSelectedAsStartDate(date) {
  return "Valgt startdato: " + date;
};

var dateIsSelectedAsEndDate = function dateIsSelectedAsEndDate(date) {
  return "Valgt sluttdato: " + date;
};

var dateIsUnavailable = function dateIsUnavailable(date) {
  return "Dato ikke tilgjengelig (" + date + ")";
};

var phrases = {
  roleDescription: roleDescription,
  jumpToNextMonth: jumpToNextMonth,
  jumpToPrevMonth: jumpToPrevMonth,
  dateIsSelected: dateIsSelected,
  dateIsUnavailable: dateIsUnavailable,
  dateIsSelectedAsStartDate: dateIsSelectedAsStartDate,
  dateIsSelectedAsEndDate: dateIsSelectedAsEndDate
};

var IDLE = 'IDLE';
var INTERACTING_START_DATE = 'INTERACTING_START_DATE';
var INTERACTING_END_DATE = 'INTERACTING_END_DATE';
var DatePickerContext = /*#__PURE__*/React.createContext({
  datepickerId: '',
  locale: nb,
  state: IDLE,
  dispatch: function dispatch() {},
  isDayDisabled: function isDayDisabled() {
    return false;
  },
  isDateRange: false,
  startInputRef: /*#__PURE__*/React.createRef(),
  endInputRef: /*#__PURE__*/React.createRef(),
  popoverRef: /*#__PURE__*/React.createRef(),
  navigationDayRef: /*#__PURE__*/React.createRef(),
  phrases: phrases,
  displayFormat: 'P',
  monthFormat: 'MMMM yyyy',
  weekDayFormat: 'EEEEEE',
  dayAriaLabelFormat: 'PPPP',
  startDate: null,
  endDate: null,
  onClear: function onClear() {},
  onChange: function onChange() {}
});

/**
 * DatePicker
 */
var DatePicker = function DatePicker(_ref) {
  var children = _ref.children,
      _ref$date = _ref.date,
      date = _ref$date === void 0 ? null : _ref$date,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? nb : _ref$locale,
      _ref$isDayDisabled = _ref.isDayDisabled,
      isDayDisabled = _ref$isDayDisabled === void 0 ? function () {
    return false;
  } : _ref$isDayDisabled,
      onChange = _ref.onChange,
      _ref$phrases = _ref.phrases,
      phrases$1 = _ref$phrases === void 0 ? phrases : _ref$phrases,
      _ref$displayFormat = _ref.displayFormat,
      displayFormat = _ref$displayFormat === void 0 ? 'P' : _ref$displayFormat,
      _ref$monthFormat = _ref.monthFormat,
      monthFormat = _ref$monthFormat === void 0 ? 'MMMM yyyy' : _ref$monthFormat,
      _ref$weekDayFormat = _ref.weekDayFormat,
      weekDayFormat = _ref$weekDayFormat === void 0 ? 'EEEEEE' : _ref$weekDayFormat,
      _ref$dayAriaLabelForm = _ref.dayAriaLabelFormat,
      dayAriaLabelFormat = _ref$dayAriaLabelForm === void 0 ? 'PPPP' : _ref$dayAriaLabelForm;
  var startInputRef = React.useRef(null);
  var endInputRef = React.useRef();
  var popoverRef = React.useRef();
  var navigationDayRef = React.useRef(null);

  var _React$useReducer = React.useReducer(datePickerReducer, {
    state: 'IDLE'
  }),
      _React$useReducer$ = _React$useReducer[0],
      state = _React$useReducer$.state,
      lastEventType = _React$useReducer$.lastEventType,
      dispatch = _React$useReducer[1];

  useFocusManagement(lastEventType, startInputRef, navigationDayRef);
  var handleClear = React.useCallback(function () {
    onChange(null);
  }, [onChange]);
  var handleChange = React.useCallback(function (day, event) {
    onChange(day);

    if (event.type === 'keydown') {
      dispatch({
        type: 'SELECT_WITH_KEYBOARD',
        day: day
      });
    } else {
      dispatch({
        type: 'SELECT_WITH_CLICK',
        day: day
      });
    }
  }, [onChange, dispatch]);
  var datepickerId = useId();
  var context = {
    datepickerId: datepickerId,
    dispatch: dispatch,
    startInputRef: startInputRef,
    endInputRef: endInputRef,
    popoverRef: popoverRef,
    navigationDayRef: navigationDayRef,
    isDayDisabled: isDayDisabled,
    isDateRange: false,
    state: state,
    locale: locale,
    startDate: date,
    endDate: null,
    phrases: phrases$1,
    displayFormat: displayFormat,
    weekDayFormat: weekDayFormat,
    monthFormat: monthFormat,
    dayAriaLabelFormat: dayAriaLabelFormat,
    onClear: handleClear,
    onChange: handleChange
  };
  return /*#__PURE__*/React.createElement(DatePickerContext.Provider, {
    value: context
  }, children);
};
DatePicker.displayName = "DatePicker";

function datePickerReducer(reducerState, event) {
  var _reducerState$lastEve;

  var lastEventType = (_reducerState$lastEve = reducerState.lastEventType) == null ? void 0 : _reducerState$lastEve.type;
  var stateTransition;

  switch (event.type) {
    case 'FOCUS':
      // Don't reopen the popover when we refocus the input field after closing the popover because of a keyboard interaction
      if (lastEventType === 'ESCAPE' || lastEventType === 'SELECT_WITH_KEYBOARD') {
        stateTransition = reducerState.state;
      } else {
        stateTransition = INTERACTING_START_DATE;
      }

      break;
    // Close the popover

    case 'BLUR':
    case 'ESCAPE':
    case 'SELECT_WITH_CLICK':
    case 'SELECT_WITH_KEYBOARD':
      stateTransition = IDLE;
      break;
    // Make sure we're interacting

    case 'NAVIGATE_FROM_INPUT':
      {
        stateTransition = INTERACTING_START_DATE;
        break;
      }
  }

  return {
    state: stateTransition,
    lastEventType: event
  };
}
/**
 * Focus handling for the datepicker.
 *
 * When the popover closes we should move focus back to the input.
 * Note that we don't do this when click the date using a mouse.
 * This is because if we want to open the popover again, we have to click
 * outside of the input, then click it again for it receive focus and show the popover
 *
 * We also move focus to the focusable date when moving from the input.
 */


function useFocusManagement(lastEvent, inputRef, navigationDayRef) {
  var lastEventType = lastEvent == null ? void 0 : lastEvent.type;
  React.useEffect(function () {
    if (lastEventType === 'ESCAPE' || lastEventType === 'SELECT_WITH_KEYBOARD') {
      var _inputRef$current;

      (_inputRef$current = inputRef.current) == null ? void 0 : _inputRef$current.focus();
    }
  }, [lastEventType, inputRef]);
  React.useEffect(function () {
    if (lastEventType === 'NAVIGATE_FROM_INPUT') {
      // need to do this in a setTimeout to be sure that the popover is open and the ref is set
      setTimeout(function () {
        var _navigationDayRef$cur;

        (_navigationDayRef$cur = navigationDayRef.current) == null ? void 0 : _navigationDayRef$cur.focus();
      });
    }
  }, [lastEventType, navigationDayRef]);
}

function isBeforeDay(date, dateToCompare) {
  if (date == null || dateToCompare == null) return false;
  return isBefore(date, dateToCompare) && !isSameDay(date, dateToCompare);
}
function isAfterDay(date, dateToCompare) {
  if (date == null || dateToCompare == null) return false;
  return !isBefore(date, dateToCompare) && !isSameDay(date, dateToCompare);
}
function isInclusivelyAfterDay(date, dateToCompare) {
  if (date == null || dateToCompare == null) return false;
  return !isBeforeDay(date, dateToCompare);
}

/**
 * DateRangePicker
 */
var DateRangePicker = function DateRangePicker(_ref) {
  var children = _ref.children,
      _ref$startDate = _ref.startDate,
      startDate = _ref$startDate === void 0 ? null : _ref$startDate,
      _ref$endDate = _ref.endDate,
      endDate = _ref$endDate === void 0 ? null : _ref$endDate,
      _ref$locale = _ref.locale,
      locale = _ref$locale === void 0 ? nb : _ref$locale,
      _ref$isDayDisabled = _ref.isDayDisabled,
      isDayDisabled = _ref$isDayDisabled === void 0 ? function () {
    return false;
  } : _ref$isDayDisabled,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? function () {} : _ref$onChange,
      _ref$phrases = _ref.phrases,
      phrases$1 = _ref$phrases === void 0 ? phrases : _ref$phrases,
      _ref$minimumNights = _ref.minimumNights,
      minimumNights = _ref$minimumNights === void 0 ? 1 : _ref$minimumNights,
      _ref$displayFormat = _ref.displayFormat,
      displayFormat = _ref$displayFormat === void 0 ? 'P' : _ref$displayFormat,
      _ref$monthFormat = _ref.monthFormat,
      monthFormat = _ref$monthFormat === void 0 ? 'MMMM yyyy' : _ref$monthFormat,
      _ref$weekDayFormat = _ref.weekDayFormat,
      weekDayFormat = _ref$weekDayFormat === void 0 ? 'EEEEEE' : _ref$weekDayFormat,
      _ref$dayAriaLabelForm = _ref.dayAriaLabelFormat,
      dayAriaLabelFormat = _ref$dayAriaLabelForm === void 0 ? 'PPPP' : _ref$dayAriaLabelForm;
  var startInputRef = React.useRef(null);
  var endInputRef = React.useRef(null);
  var popoverRef = React.useRef();
  var navigationDayRef = React.useRef(null);

  var _useEffectReducer = useEffectReducer(useDatePickerReducer({
    startDate: startDate,
    endDate: endDate,
    startInputRef: startInputRef,
    onChange: onChange,
    endInputRef: endInputRef,
    navigationDayRef: navigationDayRef,
    minimumNights: minimumNights
  }), IDLE),
      state = _useEffectReducer[0],
      dispatch = _useEffectReducer[1];

  var datepickerId = useId();
  var handleClear = React.useCallback(function () {
    if (state === INTERACTING_START_DATE) {
      onChange({
        startDate: null,
        endDate: endDate
      });
    } else if (state === INTERACTING_END_DATE) {
      onChange({
        startDate: startDate,
        endDate: null
      });
    }
  }, [onChange, startDate, endDate, state]);
  var handleChange = React.useCallback(function (day, event) {
    if (event.type === 'keydown') {
      dispatch({
        type: 'SELECT_WITH_KEYBOARD',
        day: day
      });
    } else {
      dispatch({
        type: 'SELECT_WITH_CLICK',
        day: day
      });
    }
  }, [dispatch]); // Bake in disabling of days that doesn't meet the minimum nights requirement.

  var isDayDisabledWithMinimumNights = React.useCallback(function (day) {
    if (startDate && !endDate) {
      var firstAllowedEndDate = startDate && addDays(startDate, minimumNights); // Disable all days that are between the start and first allowed end date.

      if (isAfterDay(day, startDate) && isBeforeDay(day, firstAllowedEndDate)) {
        return true;
      }
    }

    return isDayDisabled(day);
  }, [startDate, endDate, isDayDisabled, minimumNights]);
  var context = {
    datepickerId: datepickerId,
    dispatch: dispatch,
    startInputRef: startInputRef,
    endInputRef: endInputRef,
    popoverRef: popoverRef,
    navigationDayRef: navigationDayRef,
    isDayDisabled: isDayDisabledWithMinimumNights,
    isDateRange: true,
    state: state,
    locale: locale,
    startDate: startDate,
    endDate: endDate,
    phrases: phrases$1,
    displayFormat: displayFormat,
    weekDayFormat: weekDayFormat,
    monthFormat: monthFormat,
    dayAriaLabelFormat: dayAriaLabelFormat,
    onClear: handleClear,
    onChange: handleChange
  };
  return /*#__PURE__*/React.createElement(DatePickerContext.Provider, {
    value: context
  }, children);
};
DateRangePicker.displayName = "DateRangePicker";

function useDatePickerReducer(_ref2) {
  var onChange = _ref2.onChange,
      startDate = _ref2.startDate,
      endDate = _ref2.endDate,
      startInputRef = _ref2.startInputRef,
      endInputRef = _ref2.endInputRef,
      navigationDayRef = _ref2.navigationDayRef,
      minimumNights = _ref2.minimumNights;
  var lastEventRef = React.useRef();
  return function reducer(state, event, effect) {
    var _lastEventRef$current;

    var lastEventType = (_lastEventRef$current = lastEventRef.current) == null ? void 0 : _lastEventRef$current.type;
    lastEventRef.current = event;

    switch (event.type) {
      case 'FOCUS':
        // Don't reopen the popover when we refocus the input field after closing the popover with the keyboard
        if (lastEventType === 'ESCAPE' || lastEventType === 'SELECT_WITH_KEYBOARD') {
          return state;
        } // we prefer comparing with end input, so interacting with the start is the "default"


        return event.input.current === endInputRef.current ? INTERACTING_END_DATE : INTERACTING_START_DATE;

      case 'BLUR':
        return IDLE;
      // When Escape is used to close the popover, the user likely uses a screen reader, so move focus back to the inpu

      case 'ESCAPE':
        if (state === INTERACTING_START_DATE) {
          effect(function () {
            var _startInputRef$curren;

            return (_startInputRef$curren = startInputRef.current) == null ? void 0 : _startInputRef$curren.focus();
          });
        } else if (state === INTERACTING_END_DATE) {
          effect(function () {
            var _endInputRef$current;

            return (_endInputRef$current = endInputRef.current) == null ? void 0 : _endInputRef$current.focus();
          });
        }

        return IDLE;

      case 'SELECT_WITH_CLICK':
      case 'SELECT_WITH_KEYBOARD':
        return onDaySelect({
          startDate: startDate,
          endDate: endDate,
          onChange: onChange,
          day: event.day,
          state: state,
          effect: effect,
          endInputRef: endInputRef,
          startInputRef: startInputRef,
          event: event,
          minimumNights: minimumNights
        });
      // Move focus to the navigatable day

      case 'NAVIGATE_FROM_INPUT':
        {
          // need to do this in a set timeout to work correctly
          effect(function () {
            return setTimeout(function () {
              var _navigationDayRef$cur;

              return (_navigationDayRef$cur = navigationDayRef.current) == null ? void 0 : _navigationDayRef$cur.focus();
            });
          }); // we prefer comparing with end input, so interacting with the start is the "default"

          return event.input.current === endInputRef.current ? INTERACTING_END_DATE : INTERACTING_START_DATE;
        }
    }
  };
}

function onDaySelect(_ref3) {
  var startDate = _ref3.startDate,
      endDate = _ref3.endDate,
      state = _ref3.state,
      day = _ref3.day,
      onChange = _ref3.onChange,
      endInputRef = _ref3.endInputRef,
      startInputRef = _ref3.startInputRef,
      effect = _ref3.effect,
      event = _ref3.event,
      minimumNights = _ref3.minimumNights;

  if (state === INTERACTING_START_DATE) {
    var lastAllowedStartDate = endDate && subDays(endDate, minimumNights);
    var isStartDateAfterEndDate = isBeforeDay(lastAllowedStartDate, day) || isAfterDay(startDate, endDate);
    startDate = day;

    if (isStartDateAfterEndDate) {
      endDate = null;
    }

    effect(function () {
      var _endInputRef$current2;

      onChange({
        startDate: startDate,
        endDate: endDate
      });
      (_endInputRef$current2 = endInputRef.current) == null ? void 0 : _endInputRef$current2.focus();
    });
    return INTERACTING_END_DATE;
  } else if (state === INTERACTING_END_DATE) {
    var firstAllowedEndDate = startDate && addDays(startDate, minimumNights);

    if (!startDate) {
      endDate = day;
      effect(function () {
        return onChange({
          startDate: startDate,
          endDate: endDate
        });
      });
      effect(function () {
        var _startInputRef$curren2;

        return (_startInputRef$curren2 = startInputRef.current) == null ? void 0 : _startInputRef$curren2.focus();
      });
      return INTERACTING_START_DATE;
    } else if (isInclusivelyAfterDay(day, firstAllowedEndDate)) {
      endDate = day;
      effect(function () {
        return onChange({
          startDate: startDate,
          endDate: endDate
        });
      }); // If we selected the date using a keyboard we move focus back to the input when closing it

      if (event.type === 'SELECT_WITH_KEYBOARD') {
        effect(function () {
          var _endInputRef$current3;

          (_endInputRef$current3 = endInputRef.current) == null ? void 0 : _endInputRef$current3.focus();
        });
      }
    } else {
      startDate = day;
      endDate = null;
      effect(function () {
        return onChange({
          startDate: startDate,
          endDate: endDate
        });
      });
      effect(function () {
        var _endInputRef$current4;

        return (_endInputRef$current4 = endInputRef.current) == null ? void 0 : _endInputRef$current4.focus();
      });
      return INTERACTING_END_DATE;
    }
  }

  return IDLE;
}

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function useBlur() {
  var _React$useContext = React.useContext(DatePickerContext),
      dispatch = _React$useContext.dispatch,
      popoverRef = _React$useContext.popoverRef,
      startInputRef = _React$useContext.startInputRef,
      endInputRef = _React$useContext.endInputRef;

  return function handleBlur() {
    var ownerDocument = getOwnerDocument(startInputRef.current);
    if (!ownerDocument) return;
    requestAnimationFrame(function () {
      var _popoverRef$current;

      var activeElement = ownerDocument.activeElement;
      var inputIsFocused = activeElement === startInputRef.current || activeElement === endInputRef.current; // we want to close only if focus pops outside the datepicker

      if (!inputIsFocused && !((_popoverRef$current = popoverRef.current) != null && _popoverRef$current.contains(activeElement))) {
        // focus landed outside the datepicker, close it.
        dispatch({
          type: 'BLUR'
        });
      }
    });
  };
}

/**
 * DatePickerInput.
 *
 * Rembember to set `dateField` if used with `DateRangePicker`.
 */
var DatePickerInput = forwardRefWithAs(function (_ref, forwardedRef) {
  var _ref$as = _ref.as,
      Comp = _ref$as === void 0 ? 'input' : _ref$as,
      dateField = _ref.dateField,
      onBlur = _ref.onBlur,
      onChange = _ref.onChange,
      onFocus = _ref.onFocus,
      onKeyDown = _ref.onKeyDown,
      controlledValue = _ref.value,
      props = _objectWithoutPropertiesLoose(_ref, ["as", "dateField", "onBlur", "onChange", "onFocus", "onKeyDown", "value"]);

  var _React$useContext = React.useContext(DatePickerContext),
      datepickerId = _React$useContext.datepickerId,
      dispatch = _React$useContext.dispatch,
      isDayDisabled = _React$useContext.isDayDisabled,
      datepickerState = _React$useContext.state,
      startInputRef = _React$useContext.startInputRef,
      endInputRef = _React$useContext.endInputRef,
      startDate = _React$useContext.startDate,
      endDate = _React$useContext.endDate,
      locale = _React$useContext.locale,
      displayFormat = _React$useContext.displayFormat,
      onClear = _React$useContext.onClear,
      onDateChange = _React$useContext.onChange; // TODO: Add warning if dateField isn't supplied and we're rendering a date range picker


  var isStartDate = dateField !== 'endDate';
  var selectedDate = isStartDate ? startDate : endDate;
  var ref = useForkedRef(isStartDate ? startInputRef : endInputRef, forwardedRef); // We use this flag to track whether we want to synchronize the input's value with the selected date.
  // This is necessary because when we manually edit the input field and the value is not parsable as a date,
  // the selected date should be cleared, but we would still like to retain invalid date value in the input field
  // because it could be that the invalid date value is just temporary, we're working towards a valid one

  var skipInputValueSync = React.useRef(false);
  var handleBlur = useBlur();
  var valueParser = useValueParser(displayFormat, locale); // We need to partially control the input, because we want to support editing the input field to select dates.
  // If the input field is controlled, we never show this inputValue, but we will still try to parse any changes
  // into dates

  var _React$useState = React.useState(''),
      inputValue = _React$useState[0],
      setInputValue = _React$useState[1]; // we're okay with the empty string init because of the useLayoutEffect which syncs the selectedDate


  useLayoutEffect(function () {
    if (selectedDate) {
      setInputValue(format(selectedDate, displayFormat, {
        locale: locale
      }));
    } else if (!skipInputValueSync.current) {
      setInputValue('');
    }

    skipInputValueSync.current = false;
  }, [selectedDate, displayFormat, locale]);

  function handleFocus() {
    dispatch({
      type: 'FOCUS',
      input: isStartDate ? startInputRef : endInputRef
    });
  }

  function handleChange(event) {
    var value = event.target.value;
    setInputValue(value);
    var maybeDate = valueParser(value);

    if (selectedDate !== maybeDate) {
      if (maybeDate == null) {
        skipInputValueSync.current = true;
        onClear();
      } else if (!isDayDisabled(maybeDate)) {
        onDateChange(maybeDate, event);
      }
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      dispatch({
        type: 'ESCAPE'
      });
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      // prevent scrolling
      e.preventDefault();
      dispatch({
        type: 'NAVIGATE_FROM_INPUT',
        input: isStartDate ? startInputRef : endInputRef
      });
    }
  }

  return /*#__PURE__*/React.createElement(Comp, _extends({}, props, {
    "aria-haspopup": "grid",
    "aria-controls": datepickerState !== IDLE ? datepickerId : undefined,
    autoComplete: "off",
    onBlur: wrapEvent(onBlur, handleBlur),
    onChange: wrapEvent(onChange, handleChange),
    onFocus: wrapEvent(onFocus, handleFocus),
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
    ref: ref,
    value: controlledValue != null ? controlledValue : inputValue
  }));
});
/**
 * The parse function from date-fns requires a reference date to pull values from when parsing.
 * We don't really want that, as it is too easy to create wack dates just by removing a single character from the input.
 *
 * So what we do is that we format a date in the given locale to get it's length, then we use the length to compare the strings
 * before we use the date parser. This is based on the assumption that the date format length is stable, eg '31/12/2020', not '31st of December 20202'
 *
 */

function useValueParser(displayFormat, locale) {
  // Figure out the length of the string
  var dateStringLength = React.useMemo(function () {
    return format(new Date(), displayFormat, {
      locale: locale
    }).length;
  }, [displayFormat, locale]);

  var parser = function parser(dateString) {
    try {
      if (dateString.length !== dateStringLength) return null;
      var maybeDate = parse(dateString, displayFormat, new Date(), {
        locale: locale
      });
      return isValid(maybeDate) ? maybeDate : null;
    } catch (_unused) {
      return null;
    }
  };

  return parser;
}

var classes = {"popover":"DatePicker-module_popover__1kff9","navigation":"DatePicker-module_navigation__16Woo","calendar-inline":"DatePicker-module_calendar-inline__1-rW1","month":"DatePicker-module_month__VZs2-","table":"DatePicker-module_table__fWIxP","day":"DatePicker-module_day__1dHzM","weekday":"DatePicker-module_weekday__11GEr","startday":"DatePicker-module_startday__1WBIu","endday":"DatePicker-module_endday__gjBQi"};

var START_DATE = 'startDate';
var END_DATE = 'endDate';
var FOCUSED_DATE = 'focusedDate';

/**
 * DatePickerPopover
 *
 * Contains the popup that renders the calendar. Because some UI needs to render
 * more than the calendar in the popup, you need to render one of these around the
 * calendar. For example, you may want show some information about availability.
 *
 */
var DatePickerPopover = forwardRefWithAs(function (_ref, forwardedRef) {
  var _ref$attachTo = _ref.attachTo,
      attachTo = _ref$attachTo === void 0 ? 'startDate' : _ref$attachTo,
      className = _ref.className,
      onBlur = _ref.onBlur,
      onKeyDown = _ref.onKeyDown,
      props = _objectWithoutPropertiesLoose(_ref, ["attachTo", "className", "onBlur", "onKeyDown"]);

  var _React$useContext = React.useContext(DatePickerContext),
      datepickerId = _React$useContext.datepickerId,
      startInputRef = _React$useContext.startInputRef,
      endInputRef = _React$useContext.endInputRef,
      popoverRef = _React$useContext.popoverRef,
      dispatch = _React$useContext.dispatch,
      isDateRangePicker = _React$useContext.isDateRange,
      datepickerState = _React$useContext.state;

  var ref = useForkedRef(popoverRef, forwardedRef);
  var handleBlur = useBlur(); // Don't render the popover at all if we aren't active

  if (datepickerState === IDLE) return null;

  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      dispatch({
        type: 'ESCAPE'
      });
    }
  };

  var targetRef = getTargetRef({
    startInputRef: startInputRef,
    endInputRef: endInputRef,
    datepickerState: datepickerState,
    isDateRangePicker: isDateRangePicker,
    attachTo: attachTo
  });
  return /*#__PURE__*/React.createElement(Popover, _extends({}, props, {
    className: classNames(classes.popover, className),
    id: datepickerId,
    onBlur: wrapEvent(onBlur, handleBlur),
    onKeyDown: wrapEvent(onKeyDown, handleKeyDown),
    tabIndex: -1,
    targetRef: targetRef,
    ref: ref
  }));
});

function getTargetRef(_ref2) {
  var startInputRef = _ref2.startInputRef,
      endInputRef = _ref2.endInputRef,
      datepickerState = _ref2.datepickerState,
      isDateRangePicker = _ref2.isDateRangePicker,
      attachTo = _ref2.attachTo;

  // Bail out if this is a single date picker
  if (!isDateRangePicker) {
    return startInputRef;
  }

  if (attachTo === START_DATE || attachTo === FOCUSED_DATE && datepickerState === INTERACTING_START_DATE) {
    return startInputRef;
  } else if (attachTo === END_DATE || attachTo === FOCUSED_DATE && datepickerState === INTERACTING_END_DATE) {
    return endInputRef;
  }

  return startInputRef;
}

var _td;

var DatePickerDay = function DatePickerDay(_ref) {
  var _classNames;

  var month = _ref.month,
      day = _ref.day,
      navigationDate = _ref.navigationDate;

  var _React$useContext = React.useContext(DatePickerContext),
      locale = _React$useContext.locale,
      isDayDisabled = _React$useContext.isDayDisabled,
      isDateRange = _React$useContext.isDateRange,
      startDate = _React$useContext.startDate,
      endDate = _React$useContext.endDate,
      phrases = _React$useContext.phrases,
      navigationDayRef = _React$useContext.navigationDayRef,
      dayAriaLabelFormat = _React$useContext.dayAriaLabelFormat,
      onChange = _React$useContext.onChange; // Render empty cell if the day is outside of the month


  if (!isSameMonth(month, day)) {
    return _td || (_td = /*#__PURE__*/React.createElement("td", null));
  }

  var isDisabled = isDayDisabled(day);
  var isSelected = isStartOrEndDate(day, startDate, endDate);

  var handleSelect = function handleSelect(event) {
    if (isDisabled) return; // For key events we want to select on Enter and Space

    if ('key' in event) {
      if (event.key === 'Enter' || event.key === ' ') {
        // Prevents whitespace from being added to the input field
        event.preventDefault();
        onChange(day, event);
      }
    } else {
      onChange(day, event);
    }
  };

  var ariaLabel = dayAriaLabel({
    day: day,
    startDate: startDate,
    endDate: endDate,
    phrases: phrases,
    locale: locale,
    isDateRange: isDateRange,
    dayAriaLabelFormat: dayAriaLabelFormat,
    isSelected: isSelected,
    isDisabled: isDisabled
  });
  var dayInRange = isDateRange && isDayInSelectionRange(day, startDate, endDate);
  var isNavigationDate = day === navigationDate;
  return /*#__PURE__*/React.createElement("td", {
    "aria-current": isToday(day) ? 'date' : undefined,
    "aria-disabled": isDisabled,
    "aria-label": ariaLabel,
    "aria-selected": isSelected,
    className: classNames(classes.day, (_classNames = {}, _classNames[classes.startday] = startDate && endDate && isSameDay(day, startDate) && !isSameDay(startDate, endDate), _classNames[classes.endday] = startDate && endDate && isSameDay(day, endDate) && !isSameDay(startDate, endDate), _classNames)),
    "data-in-date-range": dayInRange ? '' : undefined,
    onClick: handleSelect,
    onKeyDown: handleSelect,
    ref: isNavigationDate ? navigationDayRef : undefined,
    tabIndex: isNavigationDate ? 0 : -1
  }, getDate(day));
};

DatePickerDay.displayName = "DatePickerDay";

function dayAriaLabel(_ref2) {
  var day = _ref2.day,
      startDate = _ref2.startDate,
      endDate = _ref2.endDate,
      phrases = _ref2.phrases,
      locale = _ref2.locale,
      isDateRange = _ref2.isDateRange,
      dayAriaLabelFormat = _ref2.dayAriaLabelFormat,
      isDisabled = _ref2.isDisabled,
      isSelected = _ref2.isSelected;

  if (isDateRange) {
    if (startDate && isSameDay(startDate, day)) {
      return phrases.dateIsSelectedAsStartDate(format(day, dayAriaLabelFormat, {
        locale: locale
      }));
    }

    if (endDate && isSameDay(endDate, day)) {
      return phrases.dateIsSelectedAsEndDate(format(day, dayAriaLabelFormat, {
        locale: locale
      }));
    }
  }

  if (isSelected) {
    return phrases.dateIsSelected(format(day, dayAriaLabelFormat, {
      locale: locale
    }));
  }

  if (isDisabled) {
    return phrases.dateIsUnavailable(format(day, dayAriaLabelFormat, {
      locale: locale
    }));
  }

  return format(day, dayAriaLabelFormat, {
    locale: locale
  });
}

function isStartOrEndDate(day, startDate, endDate) {
  return startDate != null && isSameDay(day, startDate) || endDate != null && isSameDay(day, endDate);
}
/**
 * Checks if the day is between the start and end day
 */


function isDayInSelectionRange(day, startDate, endDate) {
  return startDate != null && endDate != null && !isSameDay(day, startDate) && !isSameDay(day, endDate) && isWithinInterval(day, {
    start: startDate,
    end: endDate
  });
}

var DatePickerMonth = function DatePickerMonth(_ref) {
  var month = _ref.month,
      navigationDate = _ref.navigationDate;

  var _React$useContext = React.useContext(DatePickerContext),
      locale = _React$useContext.locale,
      monthFormat = _React$useContext.monthFormat,
      weekDayFormat = _React$useContext.weekDayFormat; // We know what we are doing here
  // eslint-disable-next-line react-hooks/exhaustive-deps


  var weeks = React.useMemo(function () {
    return getWeeks(month, locale);
  }, [// eslint-disable-next-line react-hooks/exhaustive-deps
  month.getMonth(), locale]); // Inserts the navigation date into the date matrix

  if (isSameMonth(navigationDate, month)) {
    var weekOfMonth = getWeekOfMonth(navigationDate, {
      locale: locale
    }) - 1; // we need to get the day of the week to index into the correct day after we've gotten the week
    // getDay() however isn't locale aware. This is a good replacement

    var day = differenceInCalendarDays(navigationDate, startOfWeek(navigationDate, {
      locale: locale
    }));
    weeks[weekOfMonth][day] = navigationDate;
  }

  return /*#__PURE__*/React.createElement("div", {
    className: classes.month
  }, /*#__PURE__*/React.createElement("div", {
    className: "u-t4 u-text-center u-mb8"
  }, format(month, monthFormat, {
    locale: locale
  })), /*#__PURE__*/React.createElement("table", {
    className: classes.table,
    role: "grid"
  }, /*#__PURE__*/React.createElement("thead", {
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement("tr", null, weeks[0].map(function (day, i) {
    return /*#__PURE__*/React.createElement("td", {
      className: classes.weekday,
      key: i
    }, format(day, weekDayFormat, {
      locale: locale
    }));
  }))), /*#__PURE__*/React.createElement("tbody", null, weeks.map(function (week, i) {
    return /*#__PURE__*/React.createElement("tr", {
      key: i
    }, week.map(function (day, i) {
      return /*#__PURE__*/React.createElement(DatePickerDay, {
        day: day,
        key: i,
        month: month,
        navigationDate: navigationDate
      });
    }));
  }))));
};
DatePickerMonth.displayName = "DatePickerMonth";

function getWeeks(month, locale) {
  var weekStarts = eachWeekOfInterval({
    start: startOfMonth(month),
    end: lastDayOfMonth(month)
  }, {
    locale: locale
  });
  var weekIntervals = weekStarts.map(function (weekStart) {
    return {
      start: weekStart,
      end: endOfWeek(weekStart, {
        locale: locale
      })
    };
  });
  return weekIntervals.map(function (week) {
    return eachDayOfInterval(week);
  });
}

var _ArrowRight, _path;

var ArrowRight = function ArrowRight(props) {
  return /*#__PURE__*/React.createElement("svg", props, _path || (_path = /*#__PURE__*/React.createElement("path", {
    fill: "currentColor",
    fillRule: "evenodd",
    d: "M5.293 3.707a1 1 0 0 1 1.414-1.414l5 5a1 1 0 0 1 0 1.414l-5 5a1 1 0 1 1-1.414-1.414L9.586 8 5.293 3.707z"
  })));
};

ArrowRight.displayName = "ArrowRight";
ArrowRight.defaultProps = {
  xmlns: "http://www.w3.org/2000/svg",
  viewBox: "0 0 16 16"
};
var DatePickerNavigation = function DatePickerNavigation(_ref) {
  var phrases = _ref.phrases,
      nextMonth = _ref.nextMonth,
      prevMonth = _ref.prevMonth;
  return /*#__PURE__*/React.createElement("div", {
    className: classes.navigation,
    "data-troika-datepicker-navigation": ""
  }, /*#__PURE__*/React.createElement("button", {
    className: "button button--pill button--small",
    "aria-label": phrases.jumpToPrevMonth,
    onClick: prevMonth
  }, /*#__PURE__*/React.createElement(ArrowRight, {
    width: "16",
    height: "16",
    style: {
      transform: 'rotate(180deg)'
    }
  })), /*#__PURE__*/React.createElement("button", {
    className: "button button--pill button--small",
    "aria-label": phrases.jumpToNextMonth,
    onClick: nextMonth
  }, _ArrowRight || (_ArrowRight = /*#__PURE__*/React.createElement(ArrowRight, {
    width: "16",
    height: "16"
  }))));
};
DatePickerNavigation.displayName = "DatePickerNavigation";

/**
 * DatePickerCalendar
 *
 * Can be used standalone, or as part of a <DatePicker /> and an input field.
 */
var DatePickerCalendar = function DatePickerCalendar(_ref) {
  var _classNames;

  var className = _ref.className,
      _ref$numberOfMonths = _ref.numberOfMonths,
      numberOfMonths = _ref$numberOfMonths === void 0 ? 1 : _ref$numberOfMonths,
      props = _objectWithoutPropertiesLoose(_ref, ["className", "numberOfMonths"]);

  var ref = React.useRef(null);

  var _React$useContext = React.useContext(DatePickerContext),
      locale = _React$useContext.locale,
      startDate = _React$useContext.startDate,
      datepickerState = _React$useContext.state,
      navigationDayRef = _React$useContext.navigationDayRef,
      phrases = _React$useContext.phrases;

  var _useNavigationDate = useNavigationDate({
    initialDate: startDate,
    calendarRef: ref,
    locale: locale,
    navigationDayRef: navigationDayRef
  }),
      navigationDate = _useNavigationDate.navigationDate,
      setNavigationDate = _useNavigationDate.setNavigationDate;

  var months = useWindowingMonths(navigationDate, numberOfMonths); // We know we are rendered inline (not in a popover) if calendar component
  // is rendered while "idle"

  var isInlineCalendar = datepickerState === IDLE;
  return /*#__PURE__*/React.createElement("div", _extends({
    className: classNames((_classNames = {}, _classNames[classes['calendar-inline']] = isInlineCalendar, _classNames), className),
    "aria-roledescription": phrases.roleDescription,
    role: isInlineCalendar ? 'region' : 'dialog',
    ref: ref
  }, props), /*#__PURE__*/React.createElement(DatePickerNavigation, {
    phrases: phrases,
    nextMonth: function nextMonth() {
      return setNavigationDate(addMonths(months[months.length - 1], 1));
    },
    prevMonth: function prevMonth() {
      return setNavigationDate(subMonths(months[0], 1));
    }
  }), months.map(function (month, i) {
    return /*#__PURE__*/React.createElement(DatePickerMonth, {
      key: i,
      month: month,
      navigationDate: navigationDate
    });
  }));
};
DatePickerCalendar.displayName = "DatePickerCalendar";

function useNavigationDate(_ref2) {
  var initialDate = _ref2.initialDate,
      calendarRef = _ref2.calendarRef,
      navigationDayRef = _ref2.navigationDayRef,
      locale = _ref2.locale;

  var _React$useState = React.useState( // Must use startOfToday() instead of just new Date() here,
  // otherwise todays date will be the only date in the calendar that doesn' start at 00:00:00
  initialDate != null ? initialDate : startOfToday()),
      navigationDate = _React$useState[0],
      setNavigationDate = _React$useState[1];

  var shouldFocusNavigationDate = React.useRef(false);
  React.useEffect(function () {
    var keyHandler = function keyHandler(event) {
      var newDate; // eslint-disable-next-line default-case

      switch (event.key) {
        case 'ArrowUp':
          newDate = subDays(navigationDate, 7);
          break;

        case 'ArrowLeft':
          newDate = subDays(navigationDate, 1);
          break;

        case 'Home':
          newDate = startOfWeek(navigationDate, {
            locale: locale
          });
          break;

        case 'PageUp':
          newDate = subMonths(navigationDate, 1);
          break;

        case 'ArrowDown':
          newDate = addDays(navigationDate, 7);
          break;

        case 'ArrowRight':
          newDate = addDays(navigationDate, 1);
          break;

        case 'End':
          newDate = endOfWeek(navigationDate, {
            locale: locale
          });
          break;

        case 'PageDown':
          newDate = addMonths(navigationDate, 1);
          break;
      }

      if (newDate) {
        // prevent scrolling
        event.preventDefault();
        setNavigationDate(newDate);
        shouldFocusNavigationDate.current = true;
      }
    };

    var calendarEl = calendarRef.current;
    calendarEl.addEventListener('keydown', keyHandler);
    return function () {
      return calendarEl.removeEventListener('keydown', keyHandler);
    };
  }, [calendarRef, locale, navigationDate]); // Focus the navigation date whenever we changed it with our keyboard shortcuts

  React.useEffect(function () {
    if (shouldFocusNavigationDate.current) {
      navigationDayRef.current.focus();
    }

    shouldFocusNavigationDate.current = false;
  }, [navigationDate, navigationDayRef]);
  return {
    navigationDate: navigationDate,
    setNavigationDate: setNavigationDate
  };
} //  TODO: Memoize? Recalculate on numberOfMonths change


function useWindowingMonths(navigationDate, numberOfMonths) {
  var intervalRef = React.useRef({
    start: startOfMonth(navigationDate),
    end: addMonths(navigationDate, numberOfMonths - 1)
  });
  var interval = intervalRef.current;

  if (isWithinInterval(navigationDate, interval)) {
    return eachMonthOfInterval(interval);
  }

  var differenceInMonths = 0;

  if (isBefore(navigationDate, interval.start)) {
    differenceInMonths = differenceInCalendarMonths(interval.start, navigationDate);
  } else {
    differenceInMonths = differenceInCalendarMonths(interval.end, navigationDate);
  }

  intervalRef.current = {
    start: startOfMonth(subMonths(interval.start, differenceInMonths)),
    end: endOfMonth(subMonths(interval.end, differenceInMonths))
  };
  return eachMonthOfInterval(intervalRef.current);
}

export { DatePicker, DatePickerCalendar, DatePickerInput, DatePickerPopover, DateRangePicker };
//# sourceMappingURL=index.es.js.map
